import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  paginatedPcmpMarketingQueueSchema,
  paginatedPcmpProposalCorrectionSchema,
  pcmpMarketingBatchesSchema,
  pcmpMarketingQueueCountsSchema,
  PCMP_PROPOSAL_CORRECTION_TAB,
  type PaginatedPcmpMarketingQueue,
  type PaginatedPcmpProposalCorrection,
  type PcmpMarketingBatches,
  type PcmpMarketingBatchesParams,
  type PcmpMarketingQueueCounts,
  type PcmpMarketingQueueParams,
  type PcmpMarketingListTab,
  type PcmpProposalCorrectionParams,
} from '../types/pcmpMarketing.types';
import type { PcmpQueueFilters } from '../types/pcmpQueue.types';

export const pcmpMarketingQueryKeys = {
  all: () => ['pcmp-marketing'] as const,
  queues: () => [...pcmpMarketingQueryKeys.all(), 'queue'] as const,
  queue: (params: PcmpMarketingQueueParams) =>
    [...pcmpMarketingQueryKeys.queues(), params] as const,
  counts: (filters: PcmpQueueFilters) =>
    [...pcmpMarketingQueryKeys.all(), 'counts', filters] as const,
  // Under all(), so acting on a request (pcmpActionCallbacks) refreshes it.
  proposalCorrection: (params: PcmpProposalCorrectionParams) =>
    [...pcmpMarketingQueryKeys.all(), 'proposal-correction', params] as const,
  batches: (params: PcmpMarketingBatchesParams) =>
    [...pcmpMarketingQueryKeys.all(), 'batches', params] as const,
  batch: (batchNo: number, params: PcmpMarketingBatchesParams) =>
    [...pcmpMarketingQueryKeys.all(), 'batch', batchNo, params] as const,
};

/** The Batches tab: the route batches holding the viewer's proposals, newest first. */
export const pcmpMarketingBatchesQueryOptions = (params: PcmpMarketingBatchesParams) =>
  queryOptions({
    queryKey: pcmpMarketingQueryKeys.batches(params),
    queryFn: async (): Promise<PcmpMarketingBatches> => {
      const res = await apiClient.get(API.pcmpMarketing.batches, { params });
      return pcmpMarketingBatchesSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** A batch's drill-down: the viewer's share of it, listed as the All tab lists them. */
export const pcmpMarketingBatchProposalsQueryOptions = (
  batchNo: number,
  params: PcmpMarketingBatchesParams,
) =>
  queryOptions({
    queryKey: pcmpMarketingQueryKeys.batch(batchNo, params),
    queryFn: async (): Promise<PaginatedPcmpMarketingQueue> => {
      const res = await apiClient.get(API.pcmpMarketing.batchProposals(batchNo), { params });
      return paginatedPcmpMarketingQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** One batch's drill-down list as Excel (legacy LDS-820); read-only viewers too. */
export function downloadPcmpMarketingBatchXlsx(
  batchNo: number,
  filters: PcmpQueueFilters,
  date: string,
): Promise<void> {
  return downloadApiFile(
    API.pcmpMarketing.batchXlsx(batchNo),
    `PCMP_Proposal_Batch_${batchNo}_${date}.xlsx`,
    { ...filters },
  );
}

export const pcmpMarketingQueueQueryOptions = (params: PcmpMarketingQueueParams) =>
  queryOptions({
    queryKey: pcmpMarketingQueryKeys.queue(params),
    queryFn: async (): Promise<PaginatedPcmpMarketingQueue> => {
      const res = await apiClient.get(API.pcmpMarketing.queue, { params });
      return paginatedPcmpMarketingQueueSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/** The Proposal Correction tab (Admins and users granted Proposal Correction). */
export const pcmpProposalCorrectionQueryOptions = (params: PcmpProposalCorrectionParams) =>
  queryOptions({
    queryKey: pcmpMarketingQueryKeys.proposalCorrection(params),
    queryFn: async (): Promise<PaginatedPcmpProposalCorrection> => {
      const res = await apiClient.get(API.pcmpMarketing.proposalCorrection, { params });
      return paginatedPcmpProposalCorrectionSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const pcmpMarketingCountsQueryOptions = (filters: PcmpQueueFilters) =>
  queryOptions({
    queryKey: pcmpMarketingQueryKeys.counts(filters),
    queryFn: async (): Promise<PcmpMarketingQueueCounts> => {
      const res = await apiClient.get(API.pcmpMarketing.queueCounts, { params: filters });
      return pcmpMarketingQueueCountsSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/**
 * A queue tab, under the same filters, as Excel. The API takes no paging (it
 * exports every row) and refuses fields it does not know, so only the tab, the
 * explained flag and the filters go.
 */
export type PcmpMarketingExportParams = Omit<PcmpMarketingQueueParams, 'page' | 'limit'>;

/**
 * Each tab's file name, as the API names it (legacy LDS-820); its header is
 * not readable cross-origin.
 * The Batches tab lists batches, not proposals: each batch has its own file.
 */
const MARKETING_XLSX_TAGS = {
  AWAITING: 'My_Awaiting_Actions',
  ACTIONABLE: 'My_Actions',
  ALL: 'List',
  MIS_NOT_VERIFIED: 'MIS_Not_Verified',
  ON_HOLD: 'On_Hold',
  [PCMP_PROPOSAL_CORRECTION_TAB]: 'Proposal_Correction',
} as const satisfies Record<PcmpMarketingListTab, string>;

export function pcmpMarketingQueueXlsxFilename(tab: PcmpMarketingListTab, date: string): string {
  return `PCMP_Proposal_${MARKETING_XLSX_TAGS[tab]}_${date}.xlsx`;
}

/** A queue tab's rows under the filters; read-only viewers may download it too. */
export function downloadPcmpMarketingQueueXlsx(
  params: PcmpMarketingExportParams,
  date: string,
): Promise<void> {
  return downloadApiFile(
    API.pcmpMarketing.queueXlsx,
    pcmpMarketingQueueXlsxFilename(params.tab, date),
    { ...params },
  );
}

/** The Proposal Correction tab under the filters; whoever sees the tab may download it. */
export function downloadPcmpProposalCorrectionXlsx(
  filters: PcmpQueueFilters,
  date: string,
): Promise<void> {
  return downloadApiFile(
    API.pcmpMarketing.proposalCorrectionXlsx,
    pcmpMarketingQueueXlsxFilename(PCMP_PROPOSAL_CORRECTION_TAB, date),
    { ...filters },
  );
}

export function pcmpMktDisbursementsXlsxFilename(date: string): string {
  return `CMP_Market_Disbursement_${date}.xlsx`;
}

/** The proposals Marketing paid out (proof uploaded), under the queue's filters. */
export function downloadPcmpMktDisbursementsXlsx(
  filters: PcmpQueueFilters,
  date: string,
): Promise<void> {
  return downloadApiFile(
    API.pcmpMarketing.disbursementsXlsx,
    pcmpMktDisbursementsXlsxFilename(date),
    { ...filters },
  );
}
